import { useStoreLanguage } from "../../../features/store-language/i18n.ts";
import React, { useEffect, useState } from "react";
import { Image, Skeleton } from "antd";
import { ImageSquare, SquaresFour } from "@phosphor-icons/react";
import { HttpService } from "../../../utils/DesigningHttpService.js";

const imageService = new HttpService();

interface Props {
  fileId?: string | number | null;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  mediaType?: "image" | "video";
  skeletonVariant?: "default" | "category" | "product";
  enablePreview?: boolean;
  fallback?: React.ReactNode;
}

export default function ShopMedia({ fileId, alt, className, style, mediaType = "image", skeletonVariant = "default", enablePreview = false, fallback }: Props) {
  const storeLanguage = useStoreLanguage();
  const [source, setSource] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(function () {
    let active = true;
    let objectUrl = "";
    setSource("");
    setFailed(false);
    if (!fileId) return function () {};

    const storeId = /^store:([1-9][0-9]*)$/.exec(String(fileId))?.[1];
    const route = storeId ? `catalog/media/${storeId}` : mediaType === "video"
      ? `file?fileId=${encodeURIComponent(String(fileId))}`
      : `file/thumbnail?fileId=${encodeURIComponent(String(fileId))}&width=${skeletonVariant === "category" ? 192 : skeletonVariant === "product" ? 480 : 1400}`;
    imageService.getblob(route)
      .then(function (response) {
        objectUrl = URL.createObjectURL(response.data);
        if (active) setSource(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      })
      .catch(function () {
        if (active) setFailed(true);
      });

    return function () {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [storeLanguage, fileId, mediaType, skeletonVariant]);

  if (!fileId || failed) {
    if (fallback) return <>{fallback}</>;
    return (
      <span
        className={`shop-media-placeholder is-${skeletonVariant}`}
        role="img"
        aria-label={alt}
      >
        {skeletonVariant === "category" ? (
          <SquaresFour size={32} weight="duotone" aria-hidden="true" />
        ) : (
          <ImageSquare size={32} weight="duotone" aria-hidden="true" />
        )}
      </span>
    );
  }
  if (!source && fallback) {
    return <>{fallback}</>;
  }
  if (!source && skeletonVariant !== "default") {
    return (
      <span
        className={`shop-media-skeleton shop-media-skeleton--${skeletonVariant}${className ? ` ${className}` : ""}`}
        aria-hidden="true"
      >
        <span />
      </span>
    );
  }
  if (!source) {
    return <Skeleton.Image active className={className} />;
  }
  if (mediaType === "video") {
    return (
      <video
        src={source}
        controls
        preload="metadata"
        className={className}
        style={style}
      />
    );
  }
  if (enablePreview) {
    return (
      <Image
        src={source}
        alt={alt}
        className={className}
        style={style}
        preview={{ src: source }}
        onError={function () { setFailed(true); }}
      />
    );
  }
  return (
    <img
      src={source}
      alt={alt}
      className={className}
      style={style}
      decoding="async"
      onError={function () { setFailed(true); }}
    />
  );
}
